/**
 * A framework's own page, laid out like a Linear project: a header with its
 * health, lead, regulators and coverage; then the requirements it answers to
 * (each regime's controls addressed), the open findings against its
 * documents, the documents themselves, the inspector's suggestions to file
 * more and what its regulators published — with its activity and sources in
 * a side rail.
 *
 * @module routes/$org.compliance.frameworks.$frameworkId
 */
import { isOpenSuggestionStatus } from "@repo/shared/reviewCounts";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeftIcon, CheckIcon, FileTextIcon, PencilIcon, SparklesIcon, XIcon } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { ComplianceUnavailable } from "../components/regulation/ComplianceUnavailable";
import { DecisionBrief } from "../components/regulation/DecisionBrief";
import { suggestionTldr } from "../components/regulation/tldr";
import { Bone, ListSkeleton } from "../components/regulation/LoadingSkeletons";
import { RegulationPageShell } from "../components/regulation/RegulationPageShell";
import { useFindingItems } from "../components/regulation/finding/useFindingItems";
import { CoverageMeter, HealthLabel } from "../components/regulation/frameworks/FrameworkGlyphs";
import {
  ActivityCard,
  DocumentsSection,
  FindingsSection,
  PublicationsSection,
  RequirementsSection,
  SECTION_CARD,
  Section,
  SourcesCard,
} from "../components/regulation/frameworks/FrameworkDetailSections";
import { FrameworksEmpty } from "../components/regulation/frameworks/FrameworksList";
import {
  coverageOf,
  decisionsOwed,
  frameworkJurisdictions,
  frameworkRegulators,
  newestFirst,
  openFindingsByDocument,
  relatedOpenFindings,
  requirementRows,
} from "../components/regulation/frameworks/frameworkView";
import { frameworkPublications, frameworkRollup, type RegimeCoverage } from "../components/regulation/monitor/frameworkRollup";
import { PersonGlyph } from "../components/regulation/monitor/linearGlyphs";
import { formatDate } from "../components/regulation/policy/PolicyGovernanceTabs";
import { RejectDialog } from "../components/regulation/policy/RejectDialog";
import { useRegulationOrg } from "../components/regulation/useRegulationOrg";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  useFrameworks,
  useGovernanceFrameworks,
  useRecommendations,
  type GovernanceFramework,
  type Recommendation,
} from "../state/governance";
import { useOrgProfile } from "../state/orgProfile";
import { useRegulatoryUpdates } from "../state/policies";

/** The framework recommendation's target, read out of its proposed change. */
const targetFrameworkId = (recommendation: Recommendation): string =>
  String(recommendation.proposedChange["frameworkId"] ?? "");

const RECENT_PUBLICATIONS = 6;

/** One fact in the header strip: a small label over its value. */
function Fact({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 flex min-h-5 min-w-0 items-center gap-1.5 text-[13px] text-foreground">{children}</dd>
    </div>
  );
}

const None = ({ children = "—" }: { readonly children?: ReactNode }) => <span className="text-muted-foreground">{children}</span>;

/* ------------------------------------------------------------------ */
/* Edit                                                                */
/* ------------------------------------------------------------------ */

function EditFrameworkDialog({
  framework,
  open,
  onOpenChange,
  onSave,
}: {
  readonly framework: GovernanceFramework;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSave: (input: { name: string; description: string | null }) => Promise<string | null>;
}) {
  const [name, setName] = useState(framework.name);
  const [description, setDescription] = useState(framework.description ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setName(framework.name);
          setDescription(framework.description ?? "");
          setError(null);
        }
        onOpenChange(next);
      }}
    >
      <DialogPopup className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit framework</DialogTitle>
          <DialogDescription>
            The name your firm uses, and the text that tells a reader what it organises.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-4">
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-foreground">Name</span>
            <Input value={name} onChange={(event) => setName(event.currentTarget.value)} />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-foreground">About</span>
            <Textarea
              rows={5}
              value={description}
              onChange={(event) => setDescription(event.currentTarget.value)}
              placeholder="What this framework organises, who runs it, and what good looks like."
            />
          </label>
          {error !== null ? <p className="text-xs text-destructive">{error}</p> : null}
        </DialogPanel>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={busy || name.trim().length === 0}
            onClick={() => {
              setBusy(true);
              setError(null);
              void onSave({
                name: name.trim(),
                description: description.trim().length === 0 ? null : description.trim(),
              }).then((failure) => {
                setBusy(false);
                setError(failure);
                if (failure === null) onOpenChange(false);
              });
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* The page                                                            */
/* ------------------------------------------------------------------ */

/** Joins the framework's regimes to the catalogue's control counts. */
function useCoverageMap(organizationId: string | null): ReadonlyMap<string, RegimeCoverage> {
  const catalogue = useFrameworks(organizationId);
  return useMemo(
    () => new Map((catalogue.snapshot?.frameworks ?? []).map((regime): [string, RegimeCoverage] => [regime.id, regime])),
    [catalogue.snapshot],
  );
}

function SuggestionsSection({
  framework,
  organizationId,
  orgSegment,
  canManage,
  onFiled,
}: {
  readonly framework: GovernanceFramework;
  readonly organizationId: string;
  readonly orgSegment: string;
  readonly canManage: boolean;
  readonly onFiled: () => void;
}) {
  const recommendations = useRecommendations(organizationId);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<Recommendation | null>(null);
  const suggestions = (recommendations.snapshot?.recommendations ?? []).filter(
    (item) =>
      item.kind === "framework" &&
      isOpenSuggestionStatus(item.status) &&
      targetFrameworkId(item) === framework.id,
  );

  const decide = async (
    recommendation: Recommendation,
    // "apply" files the document under the framework.
    decision: "apply" | "dismiss",
    note: string | null,
  ) => {
    setBusyId(recommendation.id);
    setMessage(null);
    const error = await recommendations.decide(recommendation.id, decision, { note });
    setBusyId(null);
    setMessage(error);
    if (error === null) {
      setDismissing(null);
      onFiled();
    }
    return error;
  };

  // Quiet when there is nothing to decide: the section only appears with work in it.
  if (recommendations.unavailableReason === null && recommendations.snapshot !== null && suggestions.length === 0) {
    return null;
  }

  return (
    <Section
      id="suggestions"
      title="Inspector suggestions"
      count={recommendations.snapshot === null ? undefined : suggestions.length}
      aside={<span>documents it proposes filing here</span>}
    >
      <RejectDialog
        open={dismissing !== null}
        onOpenChange={(open) => {
          if (!open) setDismissing(null);
        }}
        what="suggestion"
        title={dismissing?.title ?? null}
        onReject={({ reason }) =>
          dismissing === null ? Promise.resolve("Nothing to decide.") : decide(dismissing, "dismiss", reason)
        }
      />
      {message !== null ? <p role="alert" className="mb-2 text-xs text-destructive">{message}</p> : null}
      {recommendations.unavailableReason !== null ? (
        <ComplianceUnavailable reason={recommendations.unavailableReason} onRetry={recommendations.refresh} />
      ) : recommendations.snapshot === null ? (
        <ListSkeleton rows={2} />
      ) : (
        <ul className={cn(SECTION_CARD, "divide-y divide-border/50")}>
          {suggestions.map((recommendation) => (
            <li key={recommendation.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-start">
              <SparklesIcon aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground max-sm:hidden" />
              <div className="min-w-0 flex-1">
                <span className="block text-[13px] font-medium text-foreground">{recommendation.title}</span>
                <DecisionBrief summary={suggestionTldr(recommendation)} label="Read full rationale">
                  <p className="whitespace-pre-line">{recommendation.rationale}</p>
                </DecisionBrief>
                {recommendation.policyId !== null ? (
                  <Link
                    to="/$org/compliance/policies/$policyId"
                    params={{ org: orgSegment, policyId: recommendation.policyId }}
                    className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <FileTextIcon aria-hidden className="size-3" />
                    {recommendation.policyName ?? "The document"}
                  </Link>
                ) : null}
              </div>
              {canManage ? (
                <div className="flex shrink-0 items-center gap-1 sm:justify-end">
                  <Button
                    type="button"
                    size="sm"
                    disabled={busyId !== null}
                    onClick={() => void decide(recommendation, "apply", null)}
                    className="h-7 cursor-pointer gap-1 px-2 text-xs"
                  >
                    <CheckIcon className="size-3.5" />
                    File it here
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={busyId !== null}
                    onClick={() => setDismissing(recommendation)}
                    className="h-7 cursor-pointer gap-1 px-2 text-xs"
                  >
                    <XIcon className="size-3.5" />
                    Reject
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function FrameworkDetail({
  framework,
  orgSegment,
  organizationId,
  coverage,
  now,
}: {
  readonly framework: GovernanceFramework;
  readonly orgSegment: string;
  readonly organizationId: string;
  readonly coverage: ReadonlyMap<string, RegimeCoverage>;
  readonly now: number;
}) {
  const { canManage } = useOrgProfile(organizationId);
  const frameworks = useGovernanceFrameworks(organizationId);
  const updates = useRegulatoryUpdates(organizationId);
  const findings = useFindingItems(organizationId);

  const requirements = useMemo(() => requirementRows(framework, coverage), [framework, coverage]);
  const related = useMemo(() => relatedOpenFindings(findings.items, framework), [findings.items, framework]);
  const openByDocument = useMemo(() => openFindingsByDocument(related), [related]);
  // The same rule the list's rollup uses: publications filed under one of its regimes.
  const publications = useMemo(
    () => newestFirst(frameworkPublications(framework, updates.snapshot?.updates ?? [])),
    [framework, updates.snapshot],
  );

  return (
    <div className="mt-8 grid gap-x-10 gap-y-8 pb-10 lg:grid-cols-[minmax(0,1fr)_19rem] xl:grid-cols-[minmax(0,1fr)_21rem]">
      <div className="min-w-0 space-y-8">
        <RequirementsSection rows={requirements} orgSegment={orgSegment} />
        <FindingsSection
          items={related}
          ready={findings.ready}
          documentCount={framework.documents.length}
          orgSegment={orgSegment}
          now={now}
        />
        <DocumentsSection documents={framework.documents} openByDocument={openByDocument} orgSegment={orgSegment} />
        <SuggestionsSection
          framework={framework}
          organizationId={organizationId}
          orgSegment={orgSegment}
          canManage={canManage}
          onFiled={frameworks.refresh}
        />
        <PublicationsSection publications={publications.slice(0, RECENT_PUBLICATIONS)} orgSegment={orgSegment} />
      </div>
      <aside aria-label="Framework activity" className="min-w-0 space-y-8">
        <ActivityCard
          publications={publications}
          decisionsOwed={decisionsOwed(publications)}
          thisWeek={framework.publicationsThisWeek}
          now={now}
        />
        <SourcesCard sources={framework.sources} now={now} />
      </aside>
    </div>
  );
}

/** The header and the first sections, as bones, while the framework loads. */
function FrameworkDetailSkeleton() {
  return (
    <div role="status" aria-label="Loading framework" aria-busy="true" className="mt-2 space-y-8">
      <div className="space-y-3">
        <Bone className="h-4 w-[28rem] max-w-full" />
        <div className="flex gap-8 border-t border-border/60 pt-4">
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className="space-y-1.5">
              <Bone className="h-3 w-14" />
              <Bone className="h-4 w-24" />
            </div>
          ))}
        </div>
      </div>
      <div className="grid gap-x-10 gap-y-8 lg:grid-cols-[minmax(0,1fr)_19rem]">
        <ListSkeleton rows={4} />
        <ListSkeleton rows={2} />
      </div>
    </div>
  );
}

function RegulationFrameworkRoute() {
  const { org: orgSegment, frameworkId } = Route.useParams();
  const { org, isLoading } = useRegulationOrg();
  const organizationId = org?.id ?? null;
  const frameworks = useGovernanceFrameworks(organizationId);
  const updates = useRegulatoryUpdates(organizationId);
  const coverage = useCoverageMap(organizationId);
  const { canManage } = useOrgProfile(organizationId);
  const [editing, setEditing] = useState(false);
  const framework = frameworks.snapshot?.frameworks.find((item) => item.id === frameworkId) ?? null;
  // One clock reading per snapshot, so the header's health and the rail agree.
  const now = useMemo(() => Date.now(), [frameworks.snapshot, updates.snapshot]);
  const rollup = useMemo(
    () => (framework === null ? null : frameworkRollup(framework, coverage, updates.snapshot?.updates ?? [], now)),
    [framework, coverage, updates.snapshot, now],
  );

  const back = (
    <Link
      to="/$org/compliance/frameworks"
      params={{ org: orgSegment }}
      className="inline-flex w-fit cursor-pointer items-center gap-1.5 rounded-sm text-xs text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
    >
      <ArrowLeftIcon className="size-3.5" />
      All frameworks
    </Link>
  );

  if (framework === null || rollup === null) {
    const loading = (frameworks.isPending && frameworks.snapshot === null) || isLoading;
    return (
      <RegulationPageShell eyebrow={back} title="Framework">
        {() =>
          loading ? (
            <FrameworkDetailSkeleton />
          ) : (
            <div className="mt-6">
              <FrameworksEmpty
                title="Framework not found"
                action={
                  <Button type="button" size="xs" variant="outline" render={<Link to="/$org/compliance/frameworks" params={{ org: orgSegment }} />}>
                    Back to all frameworks
                  </Button>
                }
              >
                It is not in {org?.name ?? "your organization"}'s record. It may have been deleted or merged.
              </FrameworksEmpty>
            </div>
          )
        }
      </RegulationPageShell>
    );
  }

  const regulators = frameworkRegulators(framework);
  const jurisdictions = frameworkJurisdictions(framework);
  const total = coverageOf(rollup.controls.satisfied, rollup.controls.total);

  return (
    <RegulationPageShell
      eyebrow={back}
      title={framework.name}
      titleAside={
        <span className="flex items-center gap-2">
          <span className="inline-flex h-6 items-center rounded-full border border-border/70 px-2">
            <HealthLabel health={rollup.health} title={rollup.reasons.join(" · ")} className="text-xs" />
          </span>
          {framework.domain ? (
            <span className="inline-flex h-6 items-center rounded-full bg-muted px-2 text-xs text-muted-foreground">{framework.domain}</span>
          ) : null}
        </span>
      }
      description={
        framework.description ?? (
          <span className="text-muted-foreground/80">
            No description yet. Say what it organises, who runs it and what good looks like.
          </span>
        )
      }
      meta={
        <dl className="mt-3 grid grid-cols-2 gap-x-8 gap-y-4 border-t border-border/60 pt-4 sm:grid-cols-3 xl:grid-cols-5">
          <Fact label="Coverage">
            {total.percent === null ? <None>No controls catalogued</None> : <CoverageMeter coverage={total} showCount />}
          </Fact>
          <Fact label="Lead">
            <PersonGlyph email={framework.ownerEmail} title={framework.ownerEmail ?? "No lead"} />
            {framework.ownerEmail ? <span className="truncate">{framework.ownerEmail}</span> : <None>No lead</None>}
          </Fact>
          <Fact label={regulators.length === 1 ? "Regulator" : "Regulators"}>
            {regulators.length > 0 ? <span className="truncate" title={regulators.join(", ")}>{regulators.join(", ")}</span> : <None />}
          </Fact>
          <Fact label="Jurisdiction">
            {jurisdictions.length > 0 ? <span className="truncate" title={jurisdictions.join(", ")}>{jurisdictions.join(", ")}</span> : <None />}
          </Fact>
          <Fact label="Since">{formatDate(framework.createdAt)}</Fact>
        </dl>
      }
      actions={
        canManage && organizationId !== null ? (
          <>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setEditing(true)}
              className="h-8 cursor-pointer gap-1.5 text-xs"
            >
              <PencilIcon className="size-3.5" />
              Edit
            </Button>
            <EditFrameworkDialog
              framework={framework}
              open={editing}
              onOpenChange={setEditing}
              onSave={(input) => frameworks.patch(framework.id, input)}
            />
          </>
        ) : undefined
      }
    >
      {() =>
        organizationId === null ? null : (
          <FrameworkDetail
            framework={framework}
            orgSegment={orgSegment}
            organizationId={organizationId}
            coverage={coverage}
            now={now}
          />
        )
      }
    </RegulationPageShell>
  );
}

export const Route = createFileRoute("/$org/compliance/frameworks/$frameworkId")({
  component: RegulationFrameworkRoute,
});
